import { CountryCode, isEnum } from 'corso-types';
import { useMemo, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { z } from 'zod';
import { GeoCountryCodes } from '~/utils/constants';
import { claimAddressSchema, orderAddressSchema } from '~/utils/schemas';
import Combobox from './form-controls/Combobox';
import Input from './form-controls/Input';

type Address = z.infer<typeof claimAddressSchema>;
type OrderAddress = z.infer<typeof orderAddressSchema>;

const getProvinceOptions = (countryCode?: string | null) => {
  const isCountryCode = isEnum(countryCode, CountryCode);

  if (!isCountryCode) return [];

  return GeoCountryCodes[countryCode]?.provinces
    .map((province) => ({
      label: province.name,
      value: province.code,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

export default function AddressFields({
  address,
  className,
  pick,
  requirePhone = false,
  countryCodes = Object.values(CountryCode),
}: {
  address?: Address | OrderAddress | null;
  className?: string;
  pick?: (keyof Address)[];
  requirePhone?: boolean;
  countryCodes?: CountryCode[];
}) {
  const selectedFields = useMemo(() => new Set(pick), [pick]);

  const shouldInclude = (key: keyof Address) =>
    !pick || selectedFields.has(key);

  const [selectedCountryCode, setSelectedCountryCode] =
    useState<CountryCode | null>(
      isEnum(address?.countryCode, CountryCode) ? address.countryCode : null,
    );

  const [provinceSelectOptions, setProvinceSelectOptions] = useState<
    { label: string; value: string }[]
  >(getProvinceOptions(address?.countryCode));

  const sortedCountryCodeOptions = useMemo(() => {
    const prioritized: CountryCode[] = [CountryCode.US, CountryCode.CA];

    const prioritizedOptions = prioritized
      .filter((code) => countryCodes.includes(code))
      .map((code) => ({
        label: GeoCountryCodes[code].name,
        value: code,
      }));

    return [
      ...prioritizedOptions,
      // remaining options sorted alphabetically
      ...countryCodes
        .filter((code) => !prioritized.includes(code))
        .map((code) => ({
          label: GeoCountryCodes[code].name,
          value: code,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    ];
  }, [countryCodes]);

  const onChangeCountryCode = (value?: string) => {
    setSelectedCountryCode(isEnum(value, CountryCode) ? value : null);
    setProvinceSelectOptions(getProvinceOptions(value));
  };

  return (
    <div
      className={twMerge('grid grid-cols-1 gap-4 sm:grid-cols-2', className)}
    >
      {shouldInclude('firstName') && (
        <Input
          id="firstName"
          name="address[firstName]"
          label="First Name"
          defaultValue={address?.firstName ?? ''}
          required
          autoComplete="given-name"
          type="text"
        />
      )}

      {shouldInclude('lastName') && (
        <Input
          id="lastName"
          name="address[lastName]"
          label="Last Name"
          defaultValue={address?.lastName ?? ''}
          required
          autoComplete="family-name"
          type="text"
        />
      )}

      {shouldInclude('countryCode') && (
        <Combobox
          id="countryCode"
          name="address[countryCode]"
          label="Country"
          defaultValue={address?.countryCode ?? ''}
          required
          autoComplete="country"
          className="sm:col-span-2"
          onChange={(value) => onChangeCountryCode(value)}
          options={sortedCountryCodeOptions}
          validationMessages={{
            valueMissing: 'Please select a country code.',
          }}
        />
      )}

      {shouldInclude('line1') && (
        <Input
          id="line1"
          name="address[line1]"
          label="Address"
          defaultValue={address?.line1 ?? ''}
          required
          autoComplete="address-line1"
          type="text"
          className="sm:col-span-2"
        />
      )}

      {shouldInclude('line2') && (
        <Input
          id="line2"
          name="address[line2]"
          label="Apt, suite, etc."
          defaultValue={address?.line2 ?? ''}
          autoComplete="address-line2"
          required={false}
          type="text"
        />
      )}

      {shouldInclude('city') && (
        <Input
          id="city"
          name="address[city]"
          label="City"
          defaultValue={address?.city ?? ''}
          required
          autoComplete="address-level2"
          type="text"
        />
      )}

      {shouldInclude('stateOrProvinceCode') &&
        (provinceSelectOptions.length > 0 ?
          <Combobox
            id="stateOrProvinceCode"
            name="address[stateOrProvinceCode]"
            label="State or Province"
            defaultValue={address?.stateOrProvinceCode ?? ''}
            // required when Provinces are available
            required
            autoComplete="address-level1"
            key={selectedCountryCode}
            validationMessages={{
              valueMissing: 'Please select a valid state or province.',
            }}
            options={provinceSelectOptions}
          />
        : <Input
            id="stateOrProvinceCode"
            name="address[stateOrProvinceCode]"
            label="State or Province Code"
            defaultValue={address?.stateOrProvinceCode ?? ''}
            autoComplete="address-level1"
            type="text"
            pattern="[A-Za-z]{2,3}"
            description="ON, NY, NSW etc."
            validationMessages={{
              valueMissing:
                'Please enter a state or province code (e.g. ON, NY, NSW etc.)',
              patternMismatch:
                'Please enter a valid two or three character state or province code (e.g. ON, NY, NSW etc.)',
            }}
          />)}

      {shouldInclude('postalCode') && (
        <Input
          id="postalCode"
          name="address[postalCode]"
          label="Postal Code"
          defaultValue={address?.postalCode ?? ''}
          required
          autoComplete="postal-code"
          type="text"
        />
      )}

      {shouldInclude('phone') && (
        <Input
          id="phone"
          name="address[phone]"
          label="Phone"
          defaultValue={address?.phone ?? ''}
          required={requirePhone}
          autoComplete="tel"
          type="tel"
        />
      )}
    </div>
  );
}
